import { useState, useEffect, useRef } from 'react'
import { api, ApiRequestError } from '@/lib/api'
import { toast } from '@/hooks/useToast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface Application {
  id: number
  name: string
  app_key: string
  created_at: string
  settings: Record<string, unknown>
}

interface PaymentConfig {
  id: number
  name: string
  channel_type: string
  is_active: string
  application_ids: number[]
}

interface EditApplicationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  application: Application
}

export function EditApplicationDialog({
  open,
  onOpenChange,
  onSuccess,
  application,
}: EditApplicationDialogProps) {
  const [name, setName] = useState('')
  const [notifyUrl, setNotifyUrl] = useState('')
  const [paymentWebhookSecret, setPaymentWebhookSecret] = useState('')
  const [includeLegacyFields, setIncludeLegacyFields] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [allConfigs, setAllConfigs] = useState<PaymentConfig[]>([])
  const [selectedConfigIds, setSelectedConfigIds] = useState<Set<number>>(
    new Set()
  )
  const [initialConfigIds, setInitialConfigIds] = useState<Set<number>>(
    new Set()
  )
  const [configsLoading, setConfigsLoading] = useState(false)
  const [configsOpen, setConfigsOpen] = useState(false)

  // Pre-populate form when dialog opens or application changes
  useEffect(() => {
    if (open) {
      setName(application.name)
      setNotifyUrl((application.settings?.notify_url as string) ?? '')
      setPaymentWebhookSecret(
        (application.settings?.payment_webhook_secret as string) ?? ''
      )
      setIncludeLegacyFields(
        (application.settings?.payment_webhook_include_legacy_fields as boolean | undefined) ?? true
      )
      setError(null)
      setLoading(false)
      setConfigsOpen(false)
      fetchConfigs()
    }
  }, [open, application])

  // Auto-focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [open])

  async function fetchConfigs() {
    setConfigsLoading(true)
    try {
      const [allRes, linkedRes] = await Promise.all([
        api.get<PaymentConfig[]>('/payment-configs', { per_page: 100 }),
        api.get<PaymentConfig[]>(
          `/applications/${application.id}/payment-configs`,
          { per_page: 100 }
        ),
      ])
      setAllConfigs(allRes.data)
      const linked = new Set(linkedRes.data.map((c) => c.id))
      setSelectedConfigIds(linked)
      setInitialConfigIds(linked)
    } catch {
      setAllConfigs([])
      setSelectedConfigIds(new Set())
      setInitialConfigIds(new Set())
    } finally {
      setConfigsLoading(false)
    }
  }

  function toggleConfig(configId: number) {
    setSelectedConfigIds((prev) => {
      const next = new Set(prev)
      if (next.has(configId)) {
        next.delete(configId)
      } else {
        next.add(configId)
      }
      return next
    })
  }

  function validateName(value: string): string | null {
    if (!value.trim()) {
      return 'Application name is required'
    }
    if (value.trim().length < 2) {
      return 'Name must be at least 2 characters'
    }
    if (value.trim().length > 100) {
      return 'Name must be less than 100 characters'
    }
    return null
  }

  function configsChanged(): boolean {
    if (selectedConfigIds.size !== initialConfigIds.size) return true
    for (const id of selectedConfigIds) {
      if (!initialConfigIds.has(id)) return true
    }
    return false
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const validationError = validateName(name)
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const payload: Record<string, unknown> = { name: name.trim() }
      const settings: Record<string, unknown> = {
        ...application.settings,
      }
      if (notifyUrl.trim()) {
        settings.notify_url = notifyUrl.trim()
      } else {
        delete settings.notify_url
      }
      if (paymentWebhookSecret.trim()) {
        settings.payment_webhook_secret = paymentWebhookSecret.trim()
      } else {
        delete settings.payment_webhook_secret
      }
      settings.payment_webhook_include_legacy_fields = includeLegacyFields
      payload.settings = settings

      await api.put(`/applications/${application.id}`, payload)

      // Update payment config associations if changed
      if (configsChanged()) {
        await api.put(`/applications/${application.id}/payment-configs`, {
          payment_config_ids: Array.from(selectedConfigIds),
        })
      }

      toast({
        title: 'Application updated',
        description: `"${name.trim()}" has been updated successfully.`,
        variant: 'success',
      })
      onOpenChange(false)
      onSuccess()
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message)
        toast({
          title: 'Failed to update application',
          description: err.message,
          variant: 'destructive',
        })
      } else {
        setError('An unexpected error occurred')
        toast({
          title: 'Error',
          description: 'An unexpected error occurred',
          variant: 'destructive',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Application</DialogTitle>
            <DialogDescription>
              Update your application settings.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-app-name">Application Name</Label>
              <Input
                id="edit-app-name"
                ref={inputRef}
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  if (error) setError(null)
                }}
                placeholder="My Application"
                disabled={loading}
                aria-invalid={!!error}
                aria-describedby={error ? 'edit-app-name-error' : undefined}
              />
              {error && (
                <p
                  id="edit-app-name-error"
                  className="text-sm text-destructive"
                  role="alert"
                >
                  {error}
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-app-notify-url">Default Callback URL</Label>
              <Input
                id="edit-app-notify-url"
                value={notifyUrl}
                onChange={(e) => setNotifyUrl(e.target.value)}
                placeholder="https://example.com/callback"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                HTTPS URL for payment notifications. Per-request URLs take
                precedence.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-app-webhook-secret">
                Webhook Signing Secret
              </Label>
              <Input
                id="edit-app-webhook-secret"
                value={paymentWebhookSecret}
                onChange={(e) => setPaymentWebhookSecret(e.target.value)}
                placeholder="Optional: override app_secret for webhook signing"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to use the app secret. Set a dedicated value if you
                want webhook verification to rotate independently.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-app-include-legacy-fields">
                Notification Rollout
              </Label>
              <label
                htmlFor="edit-app-include-legacy-fields"
                className="flex items-start gap-3 rounded-md border border-input px-3 py-2 text-sm"
              >
                <input
                  id="edit-app-include-legacy-fields"
                  type="checkbox"
                  checked={includeLegacyFields}
                  onChange={(e) => setIncludeLegacyFields(e.target.checked)}
                  disabled={loading}
                  className="mt-1"
                />
                <span className="text-muted-foreground">
                  Keep top-level legacy webhook fields during migration. Disable
                  this only after downstream consumers use the envelope under
                  <code>data.object</code>.
                </span>
              </label>
            </div>
            <div className="grid gap-2">
              <Label>Payment Configs</Label>
              {configsLoading ? (
                <p className="text-sm text-muted-foreground">
                  Loading configs...
                </p>
              ) : allConfigs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No payment configs available.
                </p>
              ) : (
                <>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => setConfigsOpen((prev) => !prev)}
                    disabled={loading}
                  >
                    <span className="text-muted-foreground">
                      {selectedConfigIds.size === 0
                        ? 'Select payment configs...'
                        : `${selectedConfigIds.size} config${selectedConfigIds.size === 1 ? '' : 's'} selected`}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`h-4 w-4 opacity-50 transition-transform ${configsOpen ? 'rotate-180' : ''}`}
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                  {configsOpen && (
                    <div className="max-h-40 overflow-y-auto rounded-md border border-input">
                      {allConfigs.map((config) => (
                        <button
                          key={config.id}
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                          onClick={() => toggleConfig(config.id)}
                          disabled={loading}
                        >
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                              selectedConfigIds.has(config.id)
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-input'
                            }`}
                          >
                            {selectedConfigIds.has(config.id) && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-3 w-3"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </span>
                          <span className="flex-1 truncate">{config.name}</span>
                          <Badge variant="outline" className="shrink-0 text-xs">
                            {config.channel_type}
                          </Badge>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              <p className="text-xs text-muted-foreground">
                Link shared payment configs to this application.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <svg
                    className="mr-2 h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
